import { DEVICE_CATEGORIES, SLOT_BUSES, type DeviceCategory, type SlotBus } from './signals'

export { DEVICE_CATEGORIES, SLOT_BUSES }
export type { DeviceCategory, SlotBus }

/** Icon names are resolved by the client's icon set; the vocabulary is just strings here. */
type IconName = string

export const SLOT_BUS_LABELS: Record<SlotBus, string> = {
  PCIE: 'PCIe',
  THUNDERBOLT: 'Thunderbolt',
  OTHER: 'Other',
}

type CategoryMeta = {
  /** Group header in the library. */
  label: string
  /** Group header in the narrow canvas palette. */
  short: string
  /** One device: "Capture card". */
  singular: string
  icon: IconName
}

export const CATEGORY_META: Record<DeviceCategory, CategoryMeta> = {
  CAMERA: { label: 'Cameras', short: 'Cameras', singular: 'Camera', icon: 'eqCamera' },
  SWITCHER: { label: 'Switchers', short: 'Switchers', singular: 'Switcher', icon: 'eqSwitcher' },
  ROUTER: { label: 'Routers', short: 'Routers', singular: 'Router', icon: 'eqRouter' },
  CAPTURE_CARD: { label: 'Capture cards', short: 'Cards', singular: 'Capture card', icon: 'eqCaptureCard' },
  RECORDER: { label: 'Recorders', short: 'Recorders', singular: 'Recorder', icon: 'eqRecorder' },
  CONVERTER: { label: 'Converters', short: 'Converters', singular: 'Converter', icon: 'eqConverter' },
  ENCODER: { label: 'Streaming & encoders', short: 'Encoders', singular: 'Encoder', icon: 'eqEncoder' },
  MULTIVIEWER: { label: 'Multiviewers', short: 'Multiview', singular: 'Multiviewer', icon: 'eqMultiviewer' },
  MONITOR: { label: 'Monitors', short: 'Monitors', singular: 'Monitor', icon: 'eqMonitor' },
  AUDIO: { label: 'Audio', short: 'Audio', singular: 'Audio', icon: 'eqAudio' },
  INTERCOM: { label: 'Intercom', short: 'Intercom', singular: 'Intercom', icon: 'eqIntercom' },
  SYNC: { label: 'Sync & reference', short: 'Sync', singular: 'Sync', icon: 'eqSync' },
  NETWORK: { label: 'Network', short: 'Network', singular: 'Network', icon: 'eqNetwork' },
  POWER: { label: 'Power', short: 'Power', singular: 'Power', icon: 'eqPower' },
  COMPUTER: { label: 'Computers', short: 'Computers', singular: 'Computer', icon: 'eqComputer' },
  OTHER: { label: 'Other', short: 'Other', singular: 'Other', icon: 'eqOther' },
}

/**
 * The 17 type labels the catalogue and seed use in Device.name, mapped exactly.
 * MUST match the CASE in prisma/migrations/*_device_categories_and_computers,
 * which backfilled existing rows with the same table.
 */
export const NAME_TO_CATEGORY: Record<string, DeviceCategory> = {
  'Studio Camera': 'CAMERA',
  'System Camera': 'CAMERA',
  'Camera Control Unit': 'CAMERA',
  'Production Switcher': 'SWITCHER',
  'Capture Card': 'CAPTURE_CARD',
  'Capture Device': 'CAPTURE_CARD',
  'Capture and Playback Card': 'CAPTURE_CARD',
  'Playback Card': 'CAPTURE_CARD',
  'Disk Recorder': 'RECORDER',
  Converter: 'CONVERTER',
  'Embedder / De-embedder': 'CONVERTER',
  'Video Router': 'ROUTER',
  Multiviewer: 'MULTIVIEWER',
  'Rack Monitor': 'MONITOR',
  'Audio Console': 'AUDIO',
  'Sync Pulse Generator': 'SYNC',
  'Network Switch': 'NETWORK',
  'Rack PDU': 'POWER',
}

/** Keyword fallbacks, in the same order as the migration SQL. Order matters:
 *  "Network Switch" must land on NETWORK, not SWITCHER. */
const KEYWORDS: Array<[RegExp, DeviceCategory]> = [
  [/computer|workstation|server|\bpc\b/i, 'COMPUTER'],
  [/\bcard\b|decklink/i, 'CAPTURE_CARD'],
  [/intercom|talkback|comms/i, 'INTERCOM'],
  [/encoder|decoder|streamer/i, 'ENCODER'],
  [/network|ethernet/i, 'NETWORK'],
  [/switcher/i, 'SWITCHER'],
  [/camera|\bccu\b/i, 'CAMERA'],
  [/convert|embedd|scaler/i, 'CONVERTER'],
  [/router|videohub|matrix/i, 'ROUTER'],
  [/multiview/i, 'MULTIVIEWER'],
  [/monitor|display|scope/i, 'MONITOR'],
  [/record|hyperdeck|playback|player/i, 'RECORDER'],
  [/audio|mixer|console|\bmic\b/i, 'AUDIO'],
  [/sync|genlock|reference|timecode/i, 'SYNC'],
  [/\bpdu\b|power|\bups\b|\bpsu\b/i, 'POWER'],
]

/** Best-guess category for a device whose creator did not pick one. */
export function categoryFromName(name: string): DeviceCategory {
  const base = name.trim().replace(/ \(copy\)$/, '')
  // Own properties only: a device typed "constructor" must not inherit one.
  const exact = Object.hasOwn(NAME_TO_CATEGORY, base) ? NAME_TO_CATEGORY[base] : undefined
  if (exact) return exact
  for (const [pattern, category] of KEYWORDS) {
    if (pattern.test(base)) return category
  }
  return 'OTHER'
}

export const isComputer = (device: { category: DeviceCategory }) => device.category === 'COMPUTER'

export const isCard = (device: { category: DeviceCategory }) => device.category === 'CAPTURE_CARD'

/** Stable partition into [category, items] pairs in display order; empty groups dropped. */
export function groupByCategory<T extends { category: DeviceCategory }>(
  items: T[],
): Array<[DeviceCategory, T[]]> {
  const buckets = new Map<DeviceCategory, T[]>()
  for (const item of items) {
    const bucket = buckets.get(item.category)
    if (bucket) bucket.push(item)
    else buckets.set(item.category, [item])
  }
  return DEVICE_CATEGORIES.filter((category) => buckets.has(category)).map((category) => [
    category,
    buckets.get(category) as T[],
  ])
}

/** A library or palette section: a category, or one of the two pinned groups. */
export type ListGroup = DeviceCategory | 'FAVOURITES' | 'RECENT'

const PINNED_META: Record<'FAVOURITES' | 'RECENT', CategoryMeta> = {
  FAVOURITES: { label: 'Favourites', short: 'Favourites', singular: 'Favourite', icon: 'star' },
  RECENT: { label: 'Recently placed', short: 'Recent', singular: 'Recent', icon: 'clock' },
}

export function groupMeta(group: ListGroup): CategoryMeta {
  return group === 'FAVOURITES' || group === 'RECENT' ? PINNED_META[group] : CATEGORY_META[group]
}

/**
 * Pinned groups first — Recent in recency order, then Favourites — then the
 * categories. A device appears in exactly one group: a starred DeckLink leaves
 * its category, so a role query for its model still matches a single row.
 */
export function partitionGroups<T extends { id: string; category: DeviceCategory }>(
  items: T[],
  pins: { favouriteIds?: ReadonlySet<string>; recentIds?: readonly string[] },
): Array<[ListGroup, T[]]> {
  const byId = new Map(items.map((item) => [item.id, item]))
  const taken = new Set<string>()
  const groups: Array<[ListGroup, T[]]> = []

  const recent: T[] = []
  for (const id of pins.recentIds ?? []) {
    const item = byId.get(id)
    if (item && !taken.has(id)) {
      recent.push(item)
      taken.add(id)
    }
  }
  if (recent.length > 0) groups.push(['RECENT', recent])

  const favourites = items.filter((item) => pins.favouriteIds?.has(item.id) && !taken.has(item.id))
  if (favourites.length > 0) {
    groups.push(['FAVOURITES', favourites])
    for (const item of favourites) taken.add(item.id)
  }

  return [...groups, ...groupByCategory(items.filter((item) => !taken.has(item.id)))]
}

export function distinctBrands(items: { manufacturer: string }[]): string[] {
  return [...new Set(items.map((item) => item.manufacturer))].sort((a, b) => a.localeCompare(b))
}

/** "Slot 2 · DeckLink 8K Pro G2" — the one place a slot is written out. */
export function slotDescriptor(label: string, cardModel: string | null | undefined): string {
  return cardModel ? `${label} · ${cardModel}` : label
}
